import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MedicalRecords = () => {
    const [user, setUser] = useState(null);
    const [record, setRecord] = useState({ allergies: '', chronicConditions: '', bloodGroup: '' });
    const [reports, setReports] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    // --- STATES FOR AI UPLOAD ---
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [aiFiles, setAiFiles] = useState([]); // Store list of AI files

    useEffect(() => {
        const userString = localStorage.getItem('DDoctorUser');
        if (userString) {
            const u = JSON.parse(userString);
            setUser(u);
            fetchData(u._id);
            fetchAiFiles(u._id); // Fetch AI files on load
        }
    }, []);

    // --- 1. FETCH AI FILES ---
    const fetchAiFiles = async (userId) => {
        try {
            const res = await axios.get(`http://localhost:5030/api/user-files?userId=${userId}`);
            setAiFiles(res.data);
        } catch (err) {
            console.error("Error fetching AI files:", err);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return alert("Please select a PDF file first.");

        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', user._id);

        setIsUploading(true);
        try {
            await axios.post('http://localhost:5030/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert("Report uploaded! You can now ask the Chatbot about this file.");
            setFile(null);
            fetchAiFiles(user._id); // Refresh the list immediately
        } catch (err) {
            console.error(err);
            alert("Upload failed. Make sure Python backend (Port 5030) is running.");
        } finally {
            setIsUploading(false);
        }
    };

    const fetchData = async (userId) => {
        try {
            const recRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/medical-records/user/${userId}`);
            if (recRes.data) {
                setRecord({
                    bloodGroup: recRes.data.bloodGroup || '',
                    allergies: recRes.data.allergies ? recRes.data.allergies.join(', ') : '',
                    chronicConditions: recRes.data.chronicConditions ? recRes.data.chronicConditions.join(', ') : ''
                });
            }
            const repRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/reports/user/${userId}`);
            setReports(repRes.data);
        } catch (err) { console.error(err); }
    };

    const handleSaveRecord = async (e) => {
        e.preventDefault();
        const payload = {
            userId: user._id,
            bloodGroup: record.bloodGroup,
            allergies: record.allergies.split(',').map(s => s.trim()),
            chronicConditions: record.chronicConditions.split(',').map(s => s.trim())
        };
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/medical-records/update/${user._id}`, payload);
        setIsEditing(false);
        alert('Medical Record Updated');
    };

    if (!user) return <div className="container py-5">Please Login</div>;

    return (
        <div className="container py-5 mt-5">
            <h2 className="mb-4">My Medical Profile</h2>

            {/* --- AI REPORT UPLOAD SECTION --- */}
            <div className="card shadow-sm mb-4 border-0" style={{ background: '#f0f7ff' }}>
                <div className="card-body p-4">
                    <h5 className="fw-bold text-primary mb-3"><i className="bi bi-robot me-2"></i>Analyze Reports with AI</h5>
                    <p className="text-muted small">Upload your PDF Lab Reports here. Our AI Chatbot will read them, and you can ask questions about your results instantly.</p>

                    <form onSubmit={handleUpload} className="d-flex gap-3 align-items-center mb-4">
                        <input
                            type="file"
                            className="form-control"
                            accept="application/pdf"
                            onChange={(e) => setFile(e.target.files[0])}
                            required
                        />
                        <button
                            type="submit"
                            className="btn btn-primary fw-bold"
                            disabled={isUploading}
                            style={{ minWidth: '140px' }}
                        >
                            {isUploading ? 'Analyzing...' : 'Upload & Analyze'}
                        </button>
                    </form>

                    {/* --- DISPLAY UPLOADED AI FILES --- */}
                    {aiFiles.length > 0 && (
                        <div className="mt-3">
                            <h6 className="fw-bold text-muted small mb-2">AI Knowledge Base (Files you uploaded):</h6>
                            <ul className="list-group">
                                {aiFiles.map(file => (
                                    <li key={file._id} className="list-group-item d-flex justify-content-between align-items-center bg-transparent border-0 ps-0">
                                        <span><i className="bi bi-file-earmark-pdf text-danger me-2"></i>{file.fileName}</span>
                                        <span className="badge bg-success rounded-pill">Active</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* --- TRACK RECORD SECTION --- */}
            <div className="card shadow-sm mb-5">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Health Track Record</h5>
                    <button className="btn btn-light btn-sm" onClick={() => setIsEditing(!isEditing)}>
                        {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                </div>
                <div className="card-body">
                    {isEditing ? (
                        <form onSubmit={handleSaveRecord}>
                            <div className="mb-3">
                                <label>Blood Group</label>
                                <input className="form-control" value={record.bloodGroup} onChange={e => setRecord({ ...record, bloodGroup: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label>Allergies (comma separated)</label>
                                <input className="form-control" value={record.allergies} onChange={e => setRecord({ ...record, allergies: e.target.value })} />
                            </div>
                            <div className="mb-3">
                                <label>Chronic Conditions</label>
                                <input className="form-control" value={record.chronicConditions} onChange={e => setRecord({ ...record, chronicConditions: e.target.value })} />
                            </div>
                            <button className="btn btn-success">Save Changes</button>
                        </form>
                    ) : (
                        <div className="row">
                            <div className="col-md-4"><strong>Blood Group:</strong> {record.bloodGroup || 'N/A'}</div>
                            <div className="col-md-4"><strong>Allergies:</strong> {record.allergies || 'None'}</div>
                            <div className="col-md-4"><strong>Chronic Conditions:</strong> {record.chronicConditions || 'None'}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* --- REPORTS / HISTORY SECTION --- */}
            <h4 className="mb-3">History & Reports</h4>
            {reports.length === 0 ? (
                <div className="alert alert-info">No reports uploaded yet.</div>
            ) : (
                <div className="list-group">
                    {reports.map((rep) => (
                        <div key={rep._id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-1 fw-bold">{rep.title}</h6>
                                <small className="text-muted">{new Date(rep.date).toDateString()} - Dr. {rep.doctorName}</small>
                                <p className="mb-1 small">{rep.diagnosis}</p>
                            </div>
                            {rep.fileUrl && (
                                <a href={rep.fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                    View File
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MedicalRecords;